import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react'
import { Image, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native'
import Modal from 'react-native-modal'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { PlanLimitCode } from '@utils'
import { iconApp } from '@assets'
import { navigationRef } from '../navigationRef'
import { useLanguage } from './LanguageContext'
import { Icon, Text } from '../components/atoms'
import { INFINITY_FEATURE_KEYS } from '../screens/menu/subscription/infinityFeatures'
import { purchaseManager } from '../utils/purchases/purchaseManager'

/**
 * The reason the paywall was raised — the stable plan-limit code plus the
 * `params` the API sent (e.g. `{ limit }`), so the prompt can show a
 * specific, templated line for that limit.
 */
export interface PaywallReason {
  code?: PlanLimitCode
  params?: Record<string, unknown>
}

interface PaywallContextType {
  showPaywall: (reason?: PaywallReason) => void
  hidePaywall: () => void
}

// A no-op default keeps callers (and the axios path / tests) safe even when a
// provider is not mounted, instead of throwing mid-request.
const PaywallContext = createContext<PaywallContextType>({
  showPaywall: () => undefined,
  hidePaywall: () => undefined,
})

const durationAnimationIn = 300
const durationAnimationOut = 150

// "6,99 €/mois" → amount "6,99 €" + period "/mois", so the amount can be set large.
const splitPrice = (label: string) => {
  const slash = label.lastIndexOf('/')
  return slash > 0
    ? { amount: label.slice(0, slash).trim(), period: label.slice(slash) }
    : { amount: label, period: '' }
}

const navigateToSubscription = () => {
  if (navigationRef.isReady()) {
    navigationRef.navigate('Menu' as never, { screen: 'Subscription' } as never)
  }
}

interface PaywallProviderProps {
  children: ReactNode
}

export const PaywallProvider: React.FC<PaywallProviderProps> = ({ children }) => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const { t } = useLanguage()

  const [isVisible, setVisible] = useState(false)
  const [reason, setReason] = useState<PaywallReason | undefined>(undefined)
  const [priceLabel, setPriceLabel] = useState<string | null>(null)

  const showPaywall = useCallback((nextReason?: PaywallReason) => {
    setReason(nextReason)
    setVisible(true)
  }, [])

  const hidePaywall = useCallback(() => {
    setVisible(false)
  }, [])

  const onUpgrade = useCallback(() => {
    setVisible(false)
    navigateToSubscription()
  }, [])

  // The plan-limit code messages (with their {limit} params) double as the
  // prompt body, so a user learns exactly which limit they hit. Falls back to
  // the generic body when no specific code is available.
  const body = useMemo(() => {
    if (reason?.code) {
      const key = `errors.${reason.code}`
      const translated = t(key, reason.params)
      if (translated && translated !== key) {
        return translated
      }
    }
    return t('planLimit.body')
  }, [reason, t])

  // Same store price as the offer page; the fixed text only when the store
  // gives none.
  useEffect(() => {
    if (!isVisible || !purchaseManager.isAvailable) {
      return
    }
    let isActive = true
    purchaseManager
      .getOffering()
      .then((offering) => {
        if (isActive && offering?.priceLabel) {
          setPriceLabel(offering.priceLabel)
        }
      })
      .catch(() => undefined)
    return () => {
      isActive = false
    }
  }, [isVisible])

  const price = splitPrice(
    priceLabel
      ? t('subscription.infinityPricePerMonth', { price: priceLabel })
      : t('subscription.infinityPriceFallback'),
  )

  const value = useMemo(() => ({ showPaywall, hidePaywall }), [showPaywall, hidePaywall])

  return (
    <PaywallContext.Provider value={value}>
      {children}
      <Modal
        style={styles.modal}
        isVisible={isVisible}
        onBackdropPress={hidePaywall}
        onBackButtonPress={hidePaywall}
        animationIn="fadeIn"
        animationOut="fadeOut"
        statusBarTranslucent
        animationInTiming={durationAnimationIn}
        animationOutTiming={durationAnimationOut}
        backdropTransitionInTiming={durationAnimationIn}
        backdropTransitionOutTiming={durationAnimationOut}
      >
        <View style={styles.card}>
          <ScrollView bounces={false} showsVerticalScrollIndicator={false}>
            <View style={styles.header}>
              <View style={styles.pill}>
                <Image source={iconApp} style={styles.pillLogo} />
                <Text type="bodyExtraSmallBold" color={theme.colors.gold} style={styles.pillText}>
                  {t('subscription.infinityTierTitle').toUpperCase()}
                </Text>
              </View>
              <Text type="headline" style={styles.title}>
                {t('planLimit.title')}
              </Text>
              <Text type="body" color={theme.colors.greyBlue} style={styles.body}>
                {body}
              </Text>
            </View>

            <View style={styles.section}>
              <View style={styles.priceRow}>
                <Text type="titleBigBold" style={styles.amount}>
                  {price.amount}
                </Text>
                <Text type="body" color={theme.colors.greyBlue} style={styles.period}>
                  {price.period}
                </Text>
              </View>
              <TouchableOpacity
                testID="paywall-upgrade"
                accessibilityRole="button"
                style={styles.cta}
                onPress={onUpgrade}
                activeOpacity={0.85}
              >
                <Text type="bodyBold" color={theme.colors.white}>
                  {t('planLimit.upgrade')}
                </Text>
                <Icon name="arrowRight" size={18} color={theme.colors.white} />
              </TouchableOpacity>
              <View style={styles.reassurance}>
                <View style={styles.reassuranceItem}>
                  <Icon name="unlock" size={14} color={theme.colors.greyBlue} />
                  <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
                    {t('planLimit.noCommitment')}
                  </Text>
                </View>
                <View style={styles.reassuranceItem}>
                  <Icon name="clock" size={14} color={theme.colors.greyBlue} />
                  <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
                    {t('planLimit.cancelAnytime')}
                  </Text>
                </View>
              </View>
            </View>

            <View style={[styles.section, styles.included]}>
              <Text
                type="bodyExtraSmallBold"
                color={theme.colors.greyBlue}
                style={styles.includedLabel}
              >
                {t('planLimit.included').toUpperCase()}
              </Text>
              {INFINITY_FEATURE_KEYS.map((key) => (
                <View key={key} style={styles.feature}>
                  <View style={styles.featureCheck}>
                    <Icon name="check" size={12} color={theme.colors.gold} />
                  </View>
                  <Text type="bodySmall" style={styles.featureText}>
                    {t(key)}
                  </Text>
                </View>
              ))}
            </View>
          </ScrollView>

          {/* Clearly visible close button, top-right of the card. */}
          <TouchableOpacity
            testID="paywall-close"
            accessibilityRole="button"
            accessibilityLabel={t('planLimit.close')}
            style={styles.close}
            onPress={hidePaywall}
            hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
            activeOpacity={0.7}
          >
            <Icon name="cross" size={18} color={theme.colors.black} />
          </TouchableOpacity>
        </View>
      </Modal>
    </PaywallContext.Provider>
  )
}

export const usePaywall = (): PaywallContextType => useContext(PaywallContext)

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    modal: {
      flex: 1,
      margin: 0,
      paddingHorizontal: theme.spacing.lg,
      justifyContent: 'center',
      alignItems: 'center',
    },
    card: {
      width: '100%',
      maxWidth: 440,
      maxHeight: '92%',
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      overflow: 'hidden',
      shadowColor: theme.colors.black,
      shadowOpacity: 0.12,
      shadowRadius: 24,
      shadowOffset: { width: 0, height: 12 },
      elevation: 8,
    },
    close: {
      position: 'absolute',
      top: theme.spacing.md,
      right: theme.spacing.md,
      width: 36,
      height: 36,
      borderRadius: 18,
      backgroundColor: theme.colors.grey,
      alignItems: 'center',
      justifyContent: 'center',
    },
    header: {
      alignItems: 'center',
      paddingTop: theme.spacing.xxxl + theme.spacing.xs,
      paddingHorizontal: theme.spacing.xxl + theme.spacing.xs,
      paddingBottom: theme.spacing.xxl,
    },
    pill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      paddingVertical: theme.spacing.xs + 2,
      paddingLeft: theme.spacing.xs + 2,
      paddingRight: theme.spacing.md,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: `${theme.colors.gold}66`,
      backgroundColor: `${theme.colors.gold}14`,
    },
    pillLogo: {
      width: 18,
      height: 18,
      borderRadius: 9,
    },
    pillText: {
      letterSpacing: 1.4,
    },
    title: {
      textAlign: 'center',
      marginTop: theme.spacing.lg + 2,
    },
    body: {
      textAlign: 'center',
      marginTop: theme.spacing.sm + 2,
      lineHeight: 22,
    },
    section: {
      borderTopWidth: 1,
      borderTopColor: theme.colors.grey,
      paddingHorizontal: theme.spacing.xxl + theme.spacing.xs,
      paddingVertical: theme.spacing.xxl,
    },
    priceRow: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      justifyContent: 'center',
      gap: theme.spacing.xs,
    },
    amount: {
      fontSize: 44,
      lineHeight: 50,
      letterSpacing: -1,
    },
    period: {
      marginBottom: theme.spacing.sm,
    },
    cta: {
      marginTop: theme.spacing.xl,
      height: 54,
      borderRadius: 999,
      backgroundColor: theme.colors.gold,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.sm,
      shadowColor: theme.colors.gold,
      shadowOpacity: 0.35,
      shadowRadius: 12,
      shadowOffset: { width: 0, height: 6 },
      elevation: 4,
    },
    reassurance: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: theme.spacing.lg,
      marginTop: theme.spacing.md + 2,
    },
    reassuranceItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs + 2,
    },
    included: {
      backgroundColor: `${theme.colors.gold}0A`,
    },
    includedLabel: {
      letterSpacing: 1.2,
      marginBottom: theme.spacing.lg,
    },
    feature: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: theme.spacing.md,
      marginBottom: theme.spacing.md,
    },
    featureCheck: {
      width: 22,
      height: 22,
      borderRadius: 11,
      backgroundColor: `${theme.colors.gold}26`,
      alignItems: 'center',
      justifyContent: 'center',
    },
    featureText: {
      flex: 1,
      lineHeight: 21,
    },
  })
